import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Id } from '@contracts/common';
import type { IngestCreateBody } from '@contracts/ingest';
import type { FileTarget } from '@contracts/tasks';
import { ApiClientError } from '../../../api/client';
import { notify } from '../../../lib/toast';
import { useFileQueue, type FileQueue } from '../../tasks/FilePicker';
import { CONVERSATIONS_KEY, conversationKey } from '../api';
import { useAssistant } from '../AssistantProvider';
import { startIngest } from './api';

/** §7.1: the types the `ingest` target takes; `.csv` is named twice because browsers report it in several ways. */
export const INGEST_ACCEPT = 'image/png,image/jpeg,application/pdf,.docx,.xlsx,text/plain,.txt,.csv,text/csv';
const ACCEPT_PARTS = INGEST_ACCEPT.split(',');
const ACCEPT_EXTENSIONS = new Set(ACCEPT_PARTS.filter((a) => a.startsWith('.')));
/** The MIME types INGEST_ACCEPT names, plus what browsers report for its extensions (Word, Excel, a CSV on Windows). */
const ACCEPT_TYPES = new Set([
  ...ACCEPT_PARTS.filter((a) => !a.startsWith('.')),
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
]);

/** Whether a file is one INGEST_ACCEPT takes, by its extension or its type (what a file input's `accept` checks). */
export function ingestAccepts(file: { name: string; type: string }): boolean {
  const dot = file.name.lastIndexOf('.');
  if (dot >= 0 && ACCEPT_EXTENSIONS.has(file.name.slice(dot).toLowerCase())) return true;
  return ACCEPT_TYPES.has(file.type.toLowerCase());
}

/**
 * While a drag is still over the page only the items' types are known, not their names: an empty type (a browser
 * that cannot tell, often for .csv or .docx) may still be a file Hana reads.
 */
export function ingestMayAccept(type: string): boolean {
  return type === '' || ACCEPT_TYPES.has(type.toLowerCase());
}

/** §7.1, D54: a paste longer than this asks "Bóc dữ liệu từ đoạn này?" first. */
export const PASTE_ASK_OVER = 1500;

const TARGET: FileTarget = { kind: 'ingest' };
const FALLBACK = 'Không gửi được tài liệu. Hãy thử lại.';

/** D107: a sheet past 500 rows, refused with the ranges it splits into; "Chia lô" starts range 1 by its session. */
export type SplitOfferState = {
  sessionId: Id; rowCount: number; ranges: [number, number][];
  /** The card the sheet was dropped on (D160): "Chia lô" anchors its first range there too. */
  anchorItemId?: Id;
  /** D168: the Map drop's own hand-off, for the range "Chia lô" starts. */
  onCreated?: (jobId: Id) => void;
};
/**
 * D160: a drop onto a Map card names it; the job then reads the document as that card's (§10, §12). D168: `onCreated`
 * hears each job the pick started (a Map drop: the Map then shows that batch).
 */
export type PickOptions = { anchorItemId?: Id; onCreated?: (jobId: Id) => void };

export type IngestInput = {
  queue: FileQueue;
  /** Files picked or dropped together: they become jobs once every one of them is verified (D117). */
  pick: (files: readonly File[], opts?: PickOptions) => void;
  /** "Bóc dữ liệu" on a pasted text; true once the job exists. */
  extract: (text: string) => Promise<boolean>;
  split: SplitOfferState | null;
  splitting: boolean;
  acceptSplit: () => void;
  dismissSplit: () => void;
};

/** `seen`: the queue has shown at least one of the pick's files, so an empty match afterwards means they were removed. */
type Pick = { ids: string[]; done: boolean; seen: boolean; anchorItemId?: Id; onCreated?: (jobId: Id) => void };
/** The anchor a pick carries, as the body spreads it: nothing at all for an unanchored pick. */
const anchorOf = (anchorItemId: Id | undefined): { anchorItemId?: Id } =>
  (anchorItemId === undefined ? {} : { anchorItemId });

const isImage = (file: File) => file.type.startsWith('image/');

/**
 * The panel's way into "Luồng bóc" (§7.1): pins, drops and long pastes become `POST /api/assistant/ingest` jobs of
 * the conversation on screen. Jobs go one after another, so that a pick of several files with no conversation yet
 * creates one conversation, not one each; the first answer names it and the next posts carry it.
 */
export function useIngestInput(): IngestInput {
  const { conversationId, setConversationId } = useAssistant();
  const queryClient = useQueryClient();
  const queue = useFileQueue(TARGET);
  const picks = useRef<Pick[]>([]);
  const current = useRef(conversationId);
  useEffect(() => { current.current = conversationId; }, [conversationId]);
  const chain = useRef<Promise<unknown>>(Promise.resolve());
  const [split, setSplit] = useState<SplitOfferState | null>(null);
  const [splitting, setSplitting] = useState(false);

  /** Posts one job after the ones before it; resolves true when it was created. */
  const post = useCallback((body: IngestCreateBody, onRefused: (e: unknown) => void,
    onCreated?: (jobId: Id) => void): Promise<boolean> => {
    const run = async () => {
      const cid = current.current;
      try {
        const created = await startIngest(cid === null ? body : { ...body, conversationId: cid }, crypto.randomUUID());
        current.current = created.conversationId;
        if (created.conversationId !== cid) setConversationId(created.conversationId);
        void queryClient.invalidateQueries({ queryKey: conversationKey(created.conversationId) });
        void queryClient.invalidateQueries({ queryKey: CONVERSATIONS_KEY });
        onCreated?.(created.jobId);
        return true;
      } catch (e) {
        onRefused(e);
        return false;
      }
    };
    const next = chain.current.then(run);
    chain.current = next;
    return next;
  }, [queryClient, setConversationId]);

  const toast = useCallback((e: unknown) => {
    notify.error(e instanceof ApiClientError && e.message !== '' ? e.message : FALLBACK);
  }, []);

  const { files, drop } = queue;
  useEffect(() => {
    for (const p of picks.current) {
      if (p.done) continue;
      const entries = files.filter((f) => p.ids.includes(f.localId));
      // Every file of the pick was removed: nothing to start. Files not in the queue yet are not removed ones: a pick
      // made from an effect (a Map drop, D160) can meet this effect again before the queue's own update lands.
      if (entries.length === 0) {
        if (p.seen) p.done = true;
        continue;
      }
      p.seen = true;
      if (!entries.every((f) => f.status === 'verified' && f.uploadId !== undefined)) continue;
      p.done = true;
      const sessions = entries.map((f) => f.uploadId!);
      // D117: files picked together that are all images make one job; any other file gets its own.
      const jobs = entries.every((f) => isImage(f.file)) ? [sessions] : sessions.map((id) => [id]);
      for (const ids of jobs) {
        void post({ uploadSessionIds: ids, ...anchorOf(p.anchorItemId) }, (e) => {
          const ranges = e instanceof ApiClientError && e.code === 'INGEST_TOO_LARGE' ? e.extra?.split : undefined;
          if (ranges !== undefined && ranges.length > 0 && ids.length === 1) {
            setSplit({ sessionId: ids[0]!, rowCount: (e as ApiClientError).extra?.rowCount ?? 0, ranges,
              ...anchorOf(p.anchorItemId), ...(p.onCreated === undefined ? {} : { onCreated: p.onCreated }) });
          } else {
            toast(e);
          }
        }, p.onCreated).finally(() => drop(ids));
      }
    }
    picks.current = picks.current.filter((p) => !p.done);
  }, [files, drop, post, toast]);

  const { add } = queue;
  const pick = useCallback((chosen: readonly File[], opts: PickOptions = {}) => {
    if (chosen.length === 0) return;
    picks.current.push({ ids: add(chosen), done: false, seen: false, ...anchorOf(opts.anchorItemId),
      ...(opts.onCreated === undefined ? {} : { onCreated: opts.onCreated }) });
  }, [add]);

  const extract = useCallback((text: string) => post({ text }, toast), [post, toast]);

  const acceptSplit = useCallback(() => {
    if (split === null || splitting) return;
    const [from, to] = split.ranges[0]!;
    setSplitting(true);
    const body = { uploadSessionIds: [split.sessionId], rowRange: { from, to }, ...anchorOf(split.anchorItemId) };
    void post(body, toast, split.onCreated).then((ok) => {
      setSplitting(false);
      if (ok) setSplit(null);
    });
  }, [split, splitting, post, toast]);
  const dismissSplit = useCallback(() => setSplit(null), []);

  return { queue, pick, extract, split, splitting, acceptSplit, dismissSplit };
}
